import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Mail } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { useNexusAuth, nexusApi } from "@/context/NexusAuthContext";
import "@/pages/nexus/nexus.css";

export default function NexusSignIn() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const next = params.get("next") || "/nexus/account";
  const { login } = useNexusAuth() || {};
  const [mode, setMode] = useState("magic"); // "magic" | "password"
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [magicSent, setMagicSent] = useState(false);

  const submitPassword = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await login(email.trim(), pw);
      nav(next);
    } catch (err) {
      toast.error(err.response?.data?.detail || t("nexus.signIn.invalidCredentials"));
    } finally { setBusy(false); }
  };

  const submitMagic = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await nexusApi.post("/auth/magic-link/request", { email: email.trim(), return_to: next });
      setMagicSent(true);
      toast.success("Sign-in link sent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send link");
    } finally { setBusy(false); }
  };

  return (
    <div data-testid="nexus-signin" className="nx-container py-24 max-w-md mx-auto">
      <div className="nx-eyebrow mb-3">{t("nexus.signIn.eyebrow")}</div>
      <h1 className="font-serif text-3xl md:text-4xl text-white mb-6">{t("nexus.signIn.title")}</h1>

      <div className="mb-6 flex gap-2 border border-white/10 p-1 rounded-sm" data-testid="signin-mode-tabs">
        <button
          type="button"
          onClick={() => { setMode("magic"); setMagicSent(false); }}
          className={`flex-1 py-2 text-xs font-mono uppercase tracking-[0.18em] transition-colors ${mode === "magic" ? "bg-[#C9896E] text-black" : "text-white/60 hover:text-white"}`}
          data-testid="signin-mode-magic"
        >
          Email link
        </button>
        <button
          type="button"
          onClick={() => { setMode("password"); setMagicSent(false); }}
          className={`flex-1 py-2 text-xs font-mono uppercase tracking-[0.18em] transition-colors ${mode === "password" ? "bg-[#C9896E] text-black" : "text-white/60 hover:text-white"}`}
          data-testid="signin-mode-password"
        >
          Password
        </button>
      </div>

      {mode === "magic" ? (
        magicSent ? (
          <div className="border border-[#C9896E]/30 bg-[#C9896E]/5 p-6" data-testid="magic-sent-panel">
            <Mail className="text-[#C9896E] mb-3" size={22} />
            <div className="font-serif text-xl text-white mb-2">Check your inbox</div>
            <p className="text-sm text-white/70 leading-relaxed">
              A one-time sign-in link has been sent to <span className="text-white">{email}</span>. Click it to access your buyer dashboard. The link expires in 30 minutes.
            </p>
            <button type="button" onClick={() => setMagicSent(false)} className="mt-4 text-xs font-mono uppercase tracking-[0.18em] text-[#C9896E] hover:text-white" data-testid="magic-resend">
              Send another link
            </button>
          </div>
        ) : (
          <form onSubmit={submitMagic} className="space-y-5" data-testid="signin-magic-form">
            <div>
              <label className="nx-label">{t("nexus.signIn.email")}</label>
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="nx-field" data-testid="signin-email" />
            </div>
            <button type="submit" className="nx-btn-primary w-full justify-center" disabled={busy} data-testid="signin-magic-submit">
              {busy ? "Sending…" : <>Email me a sign-in link <ArrowRight size={14} /></>}
            </button>
            <p className="text-xs text-white/50 leading-relaxed">
              We will email a one-time link. No password required. Best for buyers who just accepted an NDA.
            </p>
          </form>
        )
      ) : (
        <form onSubmit={submitPassword} className="space-y-5" data-testid="signin-password-form">
          <div>
            <label className="nx-label">{t("nexus.signIn.email")}</label>
            <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="nx-field" data-testid="signin-email" />
          </div>
          <div>
            <label className="nx-label">{t("nexus.signIn.password")}</label>
            <input type="password" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="nx-field" data-testid="signin-password" />
          </div>
          <button type="submit" className="nx-btn-primary w-full justify-center" disabled={busy} data-testid="signin-submit">
            {busy ? t("nexus.signIn.signingIn") : <>{t("nexus.signIn.submit")} <ArrowRight size={14} /></>}
          </button>
        </form>
      )}

      <div className="mt-6 text-sm text-white/60">
        {t("nexus.signIn.newToNexus")} <Link to={`/nexus/register${next ? `?next=${encodeURIComponent(next)}` : ""}`} className="text-[#C9896E] hover:text-white">{t("nexus.signIn.qualifyLink")}</Link>
      </div>
    </div>
  );
}
